import { inject, Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { from, Subject, Subscription, timer } from 'rxjs';
import { debounceTime, filter, take, takeUntil, takeWhile } from 'rxjs/operators';

import { ModalMessageComponent } from '../../shared/modal/modal-message.component';
import * as fromRoot from '../store';
import { logger } from '../util/logger';
import { AuthenticationService } from './authentication.service';

/**
 * Service responsible for automatically logging out users after a period of inactivity.
 * 
 * Features:
 * - Tracks user activity (mouse, keyboard, touch, scroll events)
 * - Shows warning modal before logout
 * - Allows session extension through modal interaction
 * - Prevents memory leaks through proper cleanup
 * 
 * Lifecycle:
 * - Enable/disable through enable()/disable() methods
 * - Manual cleanup through destroy() method (called from AppComponent)
 */
@Injectable({ providedIn: 'root' })
export class AutoLogoutService {
  // Configuration constants
  private static readonly INACTIVITY_TIMEOUT_SECONDS = 30 * 60;
  private static readonly WARNING_PERIOD_SECONDS = 30;
  private static readonly ACTIVITY_DEBOUNCE_MS = 100;
  private static readonly TIMER_INTERVAL_MS = 1000;

  // Activity events to monitor
  private static readonly MONITORED_EVENTS = [
    'mousedown',
    'mousemove',
    'keypress',
    'scroll',
    'touchstart',
    'click'
  ] as const;

  // Service state
  private isEnabled = false;
  private lastActivityTimestamp = Date.now();
  private isWarningModalVisible = false;

  // Dependencies
  private readonly authenticationService = inject(AuthenticationService);

  // RxJS subjects and subscriptions
  private readonly destroy$ = new Subject<void>();
  private readonly resetTimer$ = new Subject<void>();
  private subscriptions: Subscription[] = [];

  // Activity listeners cleanup functions
  private activityListenersCleanup: (() => void)[] = [];

  // Timer subscriptions (tracked separately for explicit cleanup)
  private inactivityTimerSubscription?: Subscription;
  private resetTimerSubscription?: Subscription;

  constructor(
    protected store: Store<fromRoot.RootState>,
    public dialog: MatDialog,
    protected translateService: TranslateService,
  ) {
    // Service starts disabled - must be explicitly enabled
  }

  /**
   * Enables the auto-logout functionality.
   * Safe to call multiple times - prevents duplicate subscriptions.
   */
  enable(): void {
    if (this.isEnabled) {
      return; // Already enabled, prevent duplicate subscriptions
    }

    this.isEnabled = true;
    this.setupActivityListeners();
    this.resetActivityTimer();
    this.startAuthenticationMonitoring();
  }

  /**
   * Disables the auto-logout functionality and cleans up resources.
   */
  disable(): void {
    this.isEnabled = false;
    this.cleanup();
  }

  /**
   * Cleans up all subscriptions, listeners, and resources.
   */
  private cleanup(): void {
    // Unsubscribe from all tracked subscriptions
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.subscriptions.length = 0; // Clear the array

    // Clean up timer subscriptions
    if (this.inactivityTimerSubscription) {
      this.inactivityTimerSubscription.unsubscribe();
      this.inactivityTimerSubscription = undefined;
    }

    if (this.resetTimerSubscription) {
      this.resetTimerSubscription.unsubscribe();
      this.resetTimerSubscription = undefined;
    }

    // Remove activity listeners and close any open dialogs
    this.removeActivityListeners();
    this.dialog.closeAll();

    // Signal cleanup completion
    this.destroy$.next();
  }

  /**
   * Sets up DOM event listeners to detect user activity.
   * Uses passive listeners for better performance.
   */
  private setupActivityListeners(): void {
    this.removeActivityListeners();

    if (typeof document !== 'undefined') {
      AutoLogoutService.MONITORED_EVENTS.forEach(eventType => {
        try {
          const listener = () => this.onUserActivity();
          const options = { passive: true, capture: true };

          document.addEventListener(eventType, listener, options);

          // Store cleanup function
          this.activityListenersCleanup.push(() =>
            document.removeEventListener(eventType, listener, true)
          );
        } catch (error) {
          logger.error(`Failed to setup activity listener for ${eventType}: ${error}`);
        }
      });
    }
  }

  /**
   * Removes all activity event listeners.
   */
  private removeActivityListeners(): void {
    this.activityListenersCleanup.forEach(cleanup => cleanup());
    this.activityListenersCleanup.length = 0;
  }

  /**
   * Handles user activity events.
   * Resets the inactivity timer unless a warning modal is currently shown.
   */
  private onUserActivity(): void {
    if (!this.isEnabled) return;
    if (this.isWarningModalVisible) return;

    this.lastActivityTimestamp = Date.now();
    this.resetTimer$.next();
  }

  /**
   * Resets the activity timer and clears any warning state.
   */
  private resetActivityTimer(): void {
    this.lastActivityTimestamp = Date.now();
    this.isWarningModalVisible = false;
    this.resetTimer$.next();
  }

  /**
   * Starts monitoring user authentication state and begins inactivity tracking
   * once a user is logged in.
   */
  private startAuthenticationMonitoring(): void {
    const state$ = from(this.store);

    const subscription = state$
      .pipe(
        // Only start activity timer when user is logged in
        filter((rootState: fromRoot.RootState) => rootState.session.userContext.id != null),
        take(1), // Only take the first emission to start the timer once
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.startInactivityMonitoring();
      });

    this.subscriptions.push(subscription);
  }

  /**
   * Starts the inactivity monitoring timer that checks for user activity
   * and triggers warnings/logout based on configured timeouts.
   */
  private startInactivityMonitoring(): void {
    // Clean up existing timer subscriptions
    if (this.inactivityTimerSubscription) {
      this.inactivityTimerSubscription.unsubscribe();
    }

    if (this.resetTimerSubscription) {
      this.resetTimerSubscription.unsubscribe();
    }

    // Start inactivity check timer
    this.inactivityTimerSubscription = timer(
      AutoLogoutService.TIMER_INTERVAL_MS,
      AutoLogoutService.TIMER_INTERVAL_MS
    )
      .pipe(
        takeUntil(this.destroy$),
        takeWhile(() => this.isEnabled)
      )
      .subscribe(() => {
        if (!this.isEnabled) return;

        const now = Date.now();
        const inactiveTimeSeconds = Math.floor((now - this.lastActivityTimestamp) / 1000);
        const warningThreshold = AutoLogoutService.INACTIVITY_TIMEOUT_SECONDS - AutoLogoutService.WARNING_PERIOD_SECONDS;

        // Show warning when approaching timeout
        if (inactiveTimeSeconds >= warningThreshold && !this.isWarningModalVisible) {
          this.isWarningModalVisible = true;
          this.showInactivityWarning();
        }

        // Logout when timeout is reached
        if (inactiveTimeSeconds >= AutoLogoutService.INACTIVITY_TIMEOUT_SECONDS) {
          this.authenticationService.logout();
        }
      });

    // Listen for activity reset signals
    this.resetTimerSubscription = this.resetTimer$
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(AutoLogoutService.ACTIVITY_DEBOUNCE_MS)
      )
      .subscribe(() => {
        // Timer is automatically reset by updating lastActivityTimestamp in onUserActivity
      });
  }

  /**
   * Shows the inactivity warning modal to the user.
   * Allows the user to extend their session or be automatically logged out.
   */
  private showInactivityWarning(): void {
    if (!this.isEnabled) return;

    const modalData = {
      type: 'warning',
      title: this.translateService.instant('SHARED.MODAL.HEADER_INACTIVE'),
      titleCode: 'WARNING',
      message: this.translateService.instant('SHARED.MODAL.MSG_INACTIVE'),
      subMessages: [],
      timeToDoAction: AutoLogoutService.WARNING_PERIOD_SECONDS,
      redirectUrl: '/',
      buttonText: 'SHARED.BUTTONS.CLOSE',
      showExtendSession: true,
      disableClose: true,
    };

    const dialogConfig: MatDialogConfig = {
      width: '50%',
      height: 'auto',
      data: modalData,
      role: 'dialog',
      disableClose: true,
    };

    const dialogRef = this.dialog.open(ModalMessageComponent, dialogConfig);

    const dialogSubscription = dialogRef.afterClosed().subscribe(result => {
      if (result && result.extendSession) {
        this.resetActivityTimer();
      }
    });

    this.subscriptions.push(dialogSubscription);
  }

  /**
   * Destroys the service and cleans up all resources.
   * Called manually from AppComponent.ngOnDestroy().
   */
  destroy(): void {
    this.cleanup();
    this.destroy$.complete();
  }
}
